import { ToastService } from '@agilox/components';
import { inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WidgetBase } from '../../interfaces';
import { AppsettingsService } from '../services/appsettings/appsettings.service';

export abstract class CustomWidgetService<T extends WidgetBase<any>> {
	protected appSettingsService = inject(AppsettingsService);
	protected toastService = inject(ToastService);
	protected translate = inject(TranslateService);

	private _widgets = signal<T[]>([]);
	widgets = this._widgets.asReadonly();

	widgetsById(id: string) {
		this.widgets();
		return [...(this.getWidgetsById(id) ?? [])].filter(this.validateWidget.bind(this));
	}

	protected abstract getWidgetsById(id: string): T[];
	protected abstract addWidgetsToBoard(boardId: string, widgets: T[]): void;
	protected abstract validateWidget(widget: T): boolean;
	protected abstract getTranslationKey(): string;

	saveConfig(config: T, showToast: boolean = true, boardId: string): void {
		const isNew = !config.id;
		if (!config.id) {
			config.id = this.generateId();
		}

		const existingConfig = this.getWidgetsById(boardId);
		const existingIndex = existingConfig.findIndex((item) => item.id === config.id);
		if (existingIndex !== -1) {
			existingConfig[existingIndex] = config;
		} else {
			existingConfig.push(config);
		}

		this.addWidgetsToBoard(boardId, existingConfig);
		this._widgets.set([...existingConfig]);
		this.saveConfigOnServer();

		const translationKey = this.getTranslationKey();
		const operation = isNew ? 'added' : 'updated';
		if (showToast)
			this.toastService.show(this.translate.instant(`${translationKey}.${operation}`), 'success');
	}

	deleteWidget(id: string, boardId: string): void {
		const existingConfig = this.getWidgetsById(boardId);
		const existingIndex = existingConfig.findIndex((item) => item.id === id);
		if (existingIndex === -1) {
			const translationKey = this.getTranslationKey();
			this.toastService.show(this.translate.instant(`${translationKey}.remove_failure`), 'warning');
			return;
		}

		existingConfig.splice(existingIndex, 1);
		this.addWidgetsToBoard(boardId, existingConfig);
		this._widgets.set([...existingConfig]);
		this.saveConfigOnServer();

		const translationKey = this.getTranslationKey();
		this.toastService.show(this.translate.instant(`${translationKey}.removed`), 'success');
	}

	reorderWidgets(newOrderIds: string[], boardId: string): void {
		const widgets = this.getWidgetsById(boardId);
		if (!widgets || widgets.length === 0) return;

		const idToWidget = new Map(widgets.map((w) => [w.id, w]));
		const reorderedWidgets = newOrderIds.map((id) => idToWidget.get(id));

		this.addWidgetsToBoard(boardId, reorderedWidgets);

		this._widgets.set([...reorderedWidgets]);
		this.saveConfigOnServer();
	}

	saveConfigOnServer(): void {
		this.appSettingsService.saveAppSettingsOnServer();
	}

	protected generateId(): string {
		return Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
	}
}
